import { computedFrom, observable } from "aurelia-binding";
import { I18N } from "aurelia-i18n";
import { DataGridColumn, DataType, QueryFilter } from "../../../../../models/queries/queries.models";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { NotificationClientService } from "../../../../../services/basics/notification-client.service";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { QueriesClientService } from "../../../../../services/queries/queries-client.services";
import { Guid } from "../../../../../utilities/guid";
import { Reflection } from "../../../../../utilities/reflection";
import { InitFilterValueConverter } from "../../../../components/converters/queries/init-filter";
import { DataGrid } from "../../../../components/data-grid/data-grid";
import { PortsAndTerminalsService } from "../services/ports-and-terminals.service";
import { PortTerminalAndBerthResponse } from "./ports-and-terminals.models";

export interface PortRiskProfileFilterModel {
    paramFilters: ParamFilterModel[];
    portTerminalAndBerthResponse: PortTerminalAndBerthResponse;
}

export interface ParamFilterModel {
    parameterName: string;
    paramValue: any;
    dataType: DataType;
}

export type PortCallType = "pre-arrivals" | "in port" | "departures";

export interface IFilterableVesselPortList<T> {
    type: PortCallType;
    listData: T[];
    appliedFilters: QueryFilter[];
    parameterFilters: ParamFilterModel[];
    customerId: number;
    businessUnitId: number;

    applyFilters: (filters: ParamFilterModel[]) => Promise<void>;

    reloadData: () => Promise<void>;

    updateFilterData: (model: PortRiskProfileFilterModel) => void;

    getParameterValue(paramName: string, includeParamName: boolean);
}

export abstract class BasePaginatedFilterableVesselPortList<T> implements IFilterableVesselPortList<T> {
    public abstract listTitle: string;
    public abstract type: PortCallType;
    public listData: T[];
    public appliedFilters: QueryFilter[];
    parameterFilters: ParamFilterModel[];
    customerId: number;
    businessUnitId: number;
    dataGridVM: DataGrid;

    @computedFrom(
        Reflection.getPropertyPath<BasePaginatedFilterableVesselPortList<T>>(x => x.dataGridVM.unfilteredCount),
        Reflection.getPropertyPath<BasePaginatedFilterableVesselPortList<T>>(x => x.dataGridVM.pageCount),
        Reflection.getPropertyPath<BasePaginatedFilterableVesselPortList<T>>(x => x.dataGridVM.page)
    )
    public get displayingItemsText() {
        if (!this.dataGridVM) {
            return '';
        }
        const unfilteredCount = this.dataGridVM.unfilteredCount;
        const currentCount = (this.dataGridVM.currentPage || 1) * this.dataGridVM.pageCount;
        const current = currentCount > Number(unfilteredCount) ? unfilteredCount : currentCount.toString();
        const parameter0 = (!this.dataGridVM.currentPage || this.dataGridVM.currentPage - 1 == 0 ? 1 : ((this.dataGridVM.currentPage - 1) * this.dataGridVM.pageCount) + 1).toString();
        const parameter1 = current ? current.toString() : '0';
        const parameter2 = unfilteredCount ? unfilteredCount.toString() : '0';
        const parameter3 = this.listTitle;
        return this.i18N.tr('displaying-items-text').replace("{0}", parameter0).replace("{1}", parameter1).replace("{2}", parameter2).replace("{3}", parameter3);
    }

    constructor(protected i18N: I18N, protected queriesClientService: QueriesClientService, protected notificationClientService: NotificationClientService,
        protected initFilterConverter: InitFilterValueConverter, protected userClientService: UserClientService, protected requestClient: RequestClientService,
        protected portsAndTerminalsService: PortsAndTerminalsService) {

    }

    //#region search filter
    @observable()
    public searchFilter: string;

    searchFilterChanged() {
        this.applyFilters(this.parameterFilters);
    }

    getSearchFilter() {
        const searchFilters: QueryFilter[] = [];

        if (this.searchFilter) {
            const guid = Guid.generateGuid();
            searchFilters.push(QueryFilter.getQuery("vesselName", this.searchFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            searchFilters.push(QueryFilter.getQuery("vesselIdentifier", this.searchFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        return searchFilters;
    }

    clearSearchFilter() {
        this.searchFilter = null;
    }

    searchKeyDown($event) {
        if ($event?.detail) {
            const code = $event.detail.keyCode;
            if (code === 13) {
                this.applyFilters(this.parameterFilters);
            }
        }
    }
    //#endregion    

    //#region filters
    public isMobileFilterOpen: boolean;
    public selectedFilter: { title: string, clear: () => void, apply: (isMobile?: boolean) => void };

    protected async loadFiltersData() {
        const p1 = this.portsAndTerminalsService.loadPortCallStatus();
        const p2 = this.portsAndTerminalsService.loadScreeningRisks();

        const results = await Promise.all([p1, p2]);

        this.statuses = results?.length > 0 ? results[0].map(x => { return { id: x.statusId, name: x.statusName }; }) : [];
        this.riskLevels = results?.length > 1 ? results[1].map(x => { return { id: x.screeningRiskId, name: x.screeningRiskName }; }) : [];
        this.fbrConditions = [{ id: 1, name: "With feedback reports" }, { id: 2, name: "Without feedback reports" }];
    }

    public get mobileFilterSelected() {
        return this.showStatusFilter || this.showRiskLevelFilter || this.showFBRConditionFilter;
    }

    public get isSubFilterOpen(): boolean {
        return this.showStatusFilter || this.showRiskLevelFilter || this.showFBRConditionFilter;
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.selectedFilter = null;

        this.showStatusFilter = false;
        this.showRiskLevelFilter = false;
        this.showFBRConditionFilter = false;
        this.mobileRiskLevelFilterIds = [];
        this.mobileStatusFilterIds = [];
        this.mobileFBRConditionsFilterIds = [];
    }

    public closeAllFilters() {
        this.showStatusFilter = false;
        this.showRiskLevelFilter = false;
        this.showFBRConditionFilter = false;
        return false;
    }

    clearAllFilters(reload: boolean = true) {
        if(this.dataGridVM)
        {
            this.dataGridVM.clearSorting();
        }
        this.statusFilterIds = [];
        this.riskLevelFilterIds = [];
        this.fbrConditionsFilterIds = [];
        this.mobileRiskLevelFilterIds = [];
        this.mobileStatusFilterIds = [];
        this.mobileFBRConditionsFilterIds = [];

        if (reload)
            this.applyFilters(this.parameterFilters);
    }

    //#region Status Filter
    public showStatusFilter: boolean = false;
    public statusFilterIds: number[] = [];
    public mobileStatusFilterIds: number[] = [];
    public statuses: { id: number, name: string }[] = [];

    toggleStatusFilters() {
        if (!this.showStatusFilter) {
            this.closeAllFilters();
        }
        this.showStatusFilter = !this.showStatusFilter;
        this.mobileStatusFilterIds = this.statusFilterIds;
        this.selectedFilter = {
            title: "Status",
            clear: () => {
                this.statusFilterIds = [];
                this.mobileStatusFilterIds = [];
                this.showStatusFilter = false;
                this.applyFilters(this.parameterFilters);
            },
            apply: (isMobile: boolean) => {
                if (isMobile) {
                    this.statusFilterIds = this.mobileStatusFilterIds;
                }
                this.showStatusFilter = false;
                this.applyFilters(this.parameterFilters);
            }
        }
    }
    //#endregion

    //#region Risk level filter
    public showRiskLevelFilter: boolean = false;
    public riskLevelFilterIds: number[] = [];
    public mobileRiskLevelFilterIds: number[] = [];
    public riskLevels: { id: number, name: string }[] = [];

    toggleRiskLevelFilters() {
        if (!this.showRiskLevelFilter) {
            this.closeAllFilters();
        }
        this.showRiskLevelFilter = !this.showRiskLevelFilter;
        this.mobileRiskLevelFilterIds = this.riskLevelFilterIds;
        this.selectedFilter = {
            title: "Status",
            clear: () => {
                this.riskLevelFilterIds = [];
                this.mobileRiskLevelFilterIds = [];
                this.showRiskLevelFilter = false;
                this.applyFilters(this.parameterFilters);
            },
            apply: (isMobile: boolean) => {
                if (isMobile) {
                    this.riskLevelFilterIds = this.mobileRiskLevelFilterIds;
                }
                this.showRiskLevelFilter = false;
                this.applyFilters(this.parameterFilters);
            }
        }
    }
    //#endregion

    //#region FBR condition filter
    public showFBRConditionFilter: boolean = false;
    public fbrConditionsFilterIds: number[] = [];
    public mobileFBRConditionsFilterIds: number[] = [];
    public fbrConditions: { id: number, name: string }[] = [];

    toggleFBRConditionsFilters() {
        if (!this.showFBRConditionFilter) {
            this.closeAllFilters();
        }
        this.showFBRConditionFilter = !this.showFBRConditionFilter;
        this.mobileFBRConditionsFilterIds = this.fbrConditionsFilterIds;
        this.selectedFilter = {
            title: "FBR Conditions",
            clear: () => {
                this.fbrConditionsFilterIds = [];
                this.mobileFBRConditionsFilterIds = [];
                this.showFBRConditionFilter = false;
                this.applyFilters(this.parameterFilters);
            },
            apply: (isMobile: boolean) => {
                if (isMobile) {
                    this.fbrConditionsFilterIds = this.mobileFBRConditionsFilterIds;
                }
                this.showFBRConditionFilter = false;
                this.applyFilters(this.parameterFilters);
            }
        }
    }

    //#endregion

    public mobileFilterApply() {
        this.toggleMobileFilterDialog();
    }

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply(true);
            this.selectedFilter = null;
        }
    }

    public mobileClearSelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.clear();
            this.selectedFilter = null;
        }
    }
    //#endregion

    public abstract reloadData(): Promise<void>;

    async applyFilters(filters: ParamFilterModel[]): Promise<void> {
        const initialFilters = this.getInitialFilters();
        this.appliedFilters = initialFilters;
        this.parameterFilters = filters;
        await this.reloadData();
    }

    /* Refresh tab/step view-model */
    public abstract refresh(): Promise<void>;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updateFilterData(model: PortRiskProfileFilterModel) { }

    getInitialFilters() {
        const filters: QueryFilter[] = [];
        return filters;
    }

    /**
    * Get filter parameter value
    * @param paramName Parameter name
    * @param includeParamName Include parameter name
    */
    getParameterValue(paramName: string, includeParamName: boolean) {
        let paramValue = null;
        paramName = paramName.trim();

        if (this.parameterFilters?.length > 0 && paramName !== "") {
            const paramValues = this.parameterFilters.filter(x => x.parameterName.toLowerCase().trim() == paramName.toLocaleLowerCase());

            if (paramValues?.length > 0) {
                paramValue = (paramValues[0].paramValue ?
                    (
                        paramValues[0].dataType == DataType.Default && includeParamName ?
                            `'${paramValues[0].paramValue}'`
                            : `${paramValues[0].paramValue}`

                    ) : null);

                if (!includeParamName && paramValue == null) {
                    return null;
                }
                return `${includeParamName ? (paramName + "=") : ''}${paramValue}`;
            }
        }

        if (!includeParamName && paramValue == null) {
            return null;
        }
        return `${includeParamName ? (paramName + "=") : ''}null`;
    }

    protected async downloadExcel(selectColumns: DataGridColumn[] = [], filenameParam: string, limit: number = 1000) {
        this.dataGridVM.itemSource.limit = limit;
        this.dataGridVM.excelName = filenameParam;
        await this.dataGridVM.downloadExcel(selectColumns);
    }

    setPageCount(pageCount: { label: string, value: number }) {
        this.dataGridVM.pageCount = pageCount.value;
    }

}